import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Play } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const REGIONS = [["us", "United States"], ["canada", "Canada"], ["uk", "United Kingdom"], ["australia", "Australia"], ["europe", "Continental Europe"]];
const TONE = { ok: "text-emerald-700", failed: "text-red-700", running: "text-amber-700" };

function PolicyLine({ p }) {
  return (
    <p className="text-sm text-stone-700 mt-2" data-testid="fee-calendar-policy">
      Fee year shown on the site: <strong data-testid="fee-calendar-target-year">{p.target_year}</strong> (switches each {p.switch_date})
      {" · "}UK Home cap: <strong data-testid="fee-calendar-uk-cap">£{p.uk_home_cap_gbp.toLocaleString()}</strong> ({p.uk_cap_label})
      {" · "}UCAS fee: <strong data-testid="fee-calendar-ucas-fee">{p.ucas_fee_label}</strong>
    </p>
  );
}

function RunNow({ onRun, busy }) {
  const [region, setRegion] = useState("uk");
  const [check, setCheck] = useState("initial");
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4">
      <select value={region} onChange={(e) => setRegion(e.target.value)} className="border border-stone-300 rounded px-2 py-1 text-sm" data-testid="fee-calendar-region-select">
        {REGIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select value={check} onChange={(e) => setCheck(e.target.value)} className="border border-stone-300 rounded px-2 py-1 text-sm" data-testid="fee-calendar-check-select">
        <option value="initial">Initial check</option>
        <option value="recheck">Recheck</option>
      </select>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onRun(region, check)} data-testid="fee-calendar-run-now">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run now
      </Button>
    </div>
  );
}

function RunList({ runs }) {
  if (!runs.length) return <p className="text-xs text-stone-500 mt-3" data-testid="fee-calendar-no-runs">No checks have run yet.</p>;
  return (
    <ul className="mt-3 divide-y divide-stone-200" data-testid="fee-calendar-runs">
      {runs.map((r) => (
        <li key={r.key} className="py-2 text-sm" data-testid={`fee-calendar-run-${r.key}`}>
          <span className="font-medium">{r.region_label} · {r.check}</span>{" "}
          <span className={TONE[r.status] || ""}>{r.status}</span>{" "}
          <span className="text-xs text-stone-500">{(r.finished_at || r.started_at || "").slice(0, 16).replace("T", " ")} · {r.trigger}{r.emailed ? " · emailed" : ""}</span>
          {r.summary?.map((line) => <p key={line} className="text-xs text-stone-600 mt-0.5 break-words">{line}</p>)}
        </li>
      ))}
    </ul>
  );
}

export function FeeCalendarCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/fee-calendar").then(({ data }) => setData(data)).catch(() => toast.error("Could not load fee calendar")), []);
  useEffect(() => { load(); }, [load]);
  const run = async (region, check) => {
    setBusy(true);
    try {
      await api.post("/admin/fee-calendar/run", { region, check });
      toast.success("Fee check started; results appear below and by email");
      setTimeout(load, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the check"); }
    finally { setBusy(false); }
  };
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-stone-400 mt-8" data-testid="fee-calendar-loading" />;
  const next = data.schedule[0];
  return (
    <section className="mt-10 border border-stone-200 rounded-lg p-5 bg-white" data-testid="fee-calendar-card">
      <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Fee calendar</h2>
      <PolicyLine p={data.policy} />
      <p className="text-sm text-stone-600 mt-1" data-testid="fee-calendar-next">
        Next check: {next.label} {next.check} on {next.date}. {next.why}
        {data.pending_review > 0 && <span className="ml-1 text-amber-800" data-testid="fee-calendar-pending">{data.pending_review} fee change(s) over 25% waiting for review.</span>}
      </p>
      <RunNow onRun={run} busy={busy} />
      <RunList runs={data.runs} />
    </section>
  );
}
